import {
  addRatesDosagesToPlotProperties,
  removeKeys,
  splitStripGroupsIntoPlots,
} from "../legacy";
import {
  type NutrientInitialData,
  type NutrientOption,
  type NutrientState,
} from "../hooks/useNutrientState";
import { type NutrientCatalogProduct } from "../hooks/useNutrientCatalog";
import { resolveNutrientComposition } from "../../../shared/utils/nutrientCalculation";
import { type NutrientGroup } from "./nutrientPlots";
import { toMapRatesAndDosages } from "./nutrientMapRates";

export interface SavedFertiliser {
  id?: string | number;
  fertiliser?: string;
  product_id?: string;
  rate?: number | null;
  unit_of_rate?: string;
  nutrients?: Array<{ nutrient_type: string; percentage: number }>;
}

export interface NutrientTrial {
  id?: string;
  completed_step?: number;
  standard_field_properties?: {
    standard_fertilisers?: SavedFertiliser[];
    [key: string]: unknown;
  };
  trial_plots?: Array<{
    plot?: { id?: number; plot_id?: number };
    properties?: {
      fertilisers?: {
        rates_and_dosages?: Array<{
          plot_id?: number;
          rate?: number | null;
          amount?: number | null;
        }>;
      };
      [key: string]: unknown;
    };
  }>;
}

export const buildNutrientOptions = (
  products: NutrientCatalogProduct[],
  saved: SavedFertiliser[],
  nutrientCode: string
): NutrientOption[] => {
  const options = products.map((product): NutrientOption => {
    const savedFertiliser = saved.find((fertiliser) =>
      fertiliser.product_id
        ? fertiliser.product_id === product.id
        : fertiliser.fertiliser === product.name
    );
    const nutrients = resolveNutrientComposition(
      product,
      savedFertiliser?.nutrients
    );
    return {
      label: product.name,
      value: product.id,
      nutrients,
      nitrogenRatio:
        (nutrients.find((nutrient) => nutrient.nutrient_type === nutrientCode)
          ?.percentage ?? 0) / 100,
    };
  });

  saved.forEach((fertiliser) => {
    if (
      fertiliser.fertiliser &&
      !options.some((option) =>
        fertiliser.product_id
          ? option.value === fertiliser.product_id
          : option.label === fertiliser.fertiliser
      )
    ) {
      options.push({
        label: fertiliser.fertiliser,
        value: fertiliser.product_id ?? fertiliser.fertiliser,
        nutrients: fertiliser.nutrients ?? [],
        nitrogenRatio:
          (fertiliser.nutrients?.find(
            (nutrient) => nutrient.nutrient_type === nutrientCode
          )?.percentage ?? 0) / 100,
      });
    }
  });
  return options;
};

export const hydrateNutrients = (
  trial: NutrientTrial,
  groups: NutrientGroup[]
): NutrientInitialData => {
  const saved = trial.standard_field_properties?.standard_fertilisers ?? [];
  const savedRates = groups.flatMap((group, index) => {
    const plot =
      trial.trial_plots?.find(
        (entry) => String(entry.plot?.id ?? entry.plot?.plot_id) === group.id
      ) ?? trial.trial_plots?.[index];
    const rates = plot?.properties?.fertilisers?.rates_and_dosages ?? [];
    return group.plots.map((cell, cellIndex) => ({
      id: cell.id,
      entry:
        rates.find((rate) => rate.plot_id === cellIndex + 1) ??
        rates[cellIndex],
    }));
  });
  return {
    trialId: trial.id,
    fertilisers: saved.length
      ? saved.map((fertiliser, index) => ({
          id: `saved-fertiliser-${index}`,
          nutrientValue:
            fertiliser.product_id ?? fertiliser.fertiliser ?? undefined,
          rate: fertiliser.rate ?? null,
          touched: false,
        }))
      : [{ id: "fertiliser-1", rate: null, touched: false }],
    applyByPlot: Object.fromEntries(
      savedRates.map(({ id, entry }) => [id, entry?.rate ?? null])
    ),
    productKgHaByPlot: Object.fromEntries(
      savedRates.flatMap(({ id, entry }) =>
        typeof entry?.amount === "number" && Number.isFinite(entry.amount)
          ? [[id, entry.amount]]
          : []
      )
    ),
  };
};

export const buildNutrientPayload = (
  design: Parameters<typeof removeKeys>[0],
  trial: NutrientTrial,
  protocol: Parameters<typeof addRatesDosagesToPlotProperties>[1],
  state: NutrientState,
  options: NutrientOption[]
): Record<string, unknown> => {
  const standardFertilisers = state.fertilisers.map((fertiliser) => {
    const product = options.find(
      (option) =>
        option.value === fertiliser.nutrientValue ||
        option.label === fertiliser.nutrientValue
    );
    return {
      fertiliser: product?.label ?? fertiliser.nutrientValue ?? "",
      product_id: product?.value,
      rate: fertiliser.rate,
      unit_of_rate: "kg/ha",
      nutrients: product?.nutrients ?? [],
    };
  });
  const standardProperties = {
    ...trial.standard_field_properties,
    standard_fertilisers: standardFertilisers,
  };
  const mapRates = toMapRatesAndDosages(
    state.groups,
    state.applyByPlot,
    state.totalRate,
    []
  );
  const source = {
    ...design,
    trial_plots: design.trial_plots.map((plot, index) => {
      const plotProperties = "properties" in plot ? plot.properties : undefined;
      return {
        ...plot,
        plot: { ...plot.plot },
        properties: {
          ...trial.trial_plots?.[index]?.properties,
          ...(typeof plotProperties === "object" && plotProperties !== null
            ? plotProperties
            : {}),
        },
      };
    }),
    standard_field_properties: standardProperties,
    rates_and_dosages: {
      standard_fertilisers: standardFertilisers,
      rates_and_dosages: design.trial_plots.map((plot) => {
        const groupIndex = state.groups.findIndex(
          (entry) => entry.id === String(plot.plot.id)
        );
        return (mapRates[groupIndex] ?? []).map((item) => ({
          plot_id: item.plot_id,
          fertiliser_rate: item.fertiliser_rate,
          fertiliser_amount: item.fertiliser_amount,
          mapFillColor: item.mapFillColor,
        }));
      }),
    },
  };
  const transformed = addRatesDosagesToPlotProperties(source, protocol);
  const nutrientPlots = transformed.trial_plots.map(
    (plot: { properties: { fertilisers?: unknown } }, index: number) => ({
      ...source.trial_plots[index],
      properties: {
        ...source.trial_plots[index].properties,
        fertilisers: plot.properties.fertilisers,
      },
    })
  );
  const cleaned = removeKeys({ ...design, trial_plots: nutrientPlots });
  return {
    trial_plots: splitStripGroupsIntoPlots(cleaned.trial_plots),
    standard_field_properties: standardProperties,
    completed_step: Math.max(trial.completed_step ?? 0, 3),
    completion: false,
  };
};
